import { useCallback } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { PlanAndUsagePage } from '@/features/settings/plan-and-usage/plan-and-usage-page';
import type { BillingReturnParam } from '@/features/settings/plan-and-usage/use-salon-billing';
import { formatDocumentTitle } from '@/lib/document-title';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';

/** Stripe Checkout returns here with `?billing=success&session_id=cs_…` or `?billing=cancel`; handled once, then dropped. */
interface PlanAndUsageSearch {
  billing?: BillingReturnParam;
  session_id?: string;
}

export const Route = createFileRoute('/_auth/$orgId/settings/plan-and-usage')({
  validateSearch: (search: Record<string, unknown>): PlanAndUsageSearch => ({
    billing: search.billing === 'success' || search.billing === 'cancel' ? search.billing : undefined,
    session_id: typeof search.session_id === 'string' && /^cs_[A-Za-z0-9_]+$/.test(search.session_id) ? search.session_id : undefined,
  }),
  component: PlanAndUsageRoute,
});

function PlanAndUsageRoute() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/plan-and-usage' });
  const { billing, session_id: sessionId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const clearBillingParam = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, billing: undefined, session_id: undefined }), replace: true });
  }, [navigate]);
  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Plan & usage')}</title>
      </Helmet>
      <BusinessSetupPageShell orgId={orgId} activePath="plan-and-usage">
        <PlanAndUsagePage billingParam={billing} checkoutSessionId={sessionId} onBillingParamHandled={clearBillingParam} />
      </BusinessSetupPageShell>
    </>
  );
}
